import React, { useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { fetchPublicPodcast } from "src/redux/actions/coaching/podcast";
import { Podcast, PodcastTranscripts } from "src/types/coaching/podcast";

import {
  PodcastContext,
  PodcastContextProps,
} from "src/pages/coaching/components/podcast/context";
import { PodcastSummaryComponent } from "src/pages/coaching/components/podcast";
import EmptyMessage from "src/components/emptyMessage";

import _ from "lodash";
import cx from "classnames";
import styles from "./podcast.module.scss";
import { enrollPodcast, fetchLearningRecordPodcast, fetchTranscriptsForLearningPodcast, updateCourseContentSession, updatePodcastSession } from "src/redux/actions/coaching/learners";
import { LearningRecordCourse, LearningRecordPodcast, UpdateCourseContentSessionRequest, UpdatePodcastSessionRequest } from "src/types/coaching/learners";
import { useNavigate } from "react-router-dom";
import { CourseContext } from "../../../context";
import { PodcastLesson } from "src/types/coaching/courses";

interface ViewPodcastContentProps {
  course: LearningRecordCourse;
  content: PodcastLesson;
}

export const ViewPodcastContent = ({ course, content }: ViewPodcastContentProps) => {
  const courseContext = useContext(CourseContext);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const id = _.get(content, "content.id");

  const [podcastState, setPodcastState] = useState({
    learningRecord: null,
    podcast: null,
  });

  const [podcastContext, setPodcastContext] =
    React.useState<PodcastContextProps>({
      id: id,
    });

  const [autoPlay, setAutoPlay] = useState(false);

  const { creating, fetching, childLearningRecord, session, status } = useMemo(() => {
    const item = courseContext?.courseContentSession;

    const creating = _.get(item, "creating") || false;
    const fetching = _.get(item, "fetching") || false;

    const childLearningRecord = _.get(item, "childLearningRecord");
    const session = _.get(item, "session");
    const status = _.get(item, "status");

    return {
      creating,
      fetching,
      childLearningRecord,
      session,
      status,
    }
  }, [courseContext?.courseContentSession]);

  const learningRecordPodcast = useSelector((state: any) => {
    if(childLearningRecord) {
      return _.get(state.learners, `podcast[${childLearningRecord}].item`);
    }

    return null;
  });

  const publicPodcast = useSelector((state: any) => {
    return _.get(state.coaching, `podcasts.podcast[${id}].item`);
  });

  const podcast = learningRecordPodcast || publicPodcast;

  const loading = useSelector((state: any) =>
    _.get(state.coaching, `podcasts.podcast[${id}].loading`),
  );

  const podcastErrorCode = useSelector((state: any) => {
    return _.get(
      state.coaching,
      `podcasts.podcast[${id}].error.errors[0].code`,
    );
  });

  const podcastTranscripts = useSelector((state: any) => {
      if(childLearningRecord) {
        return _.get(state.learners, `podcast[${childLearningRecord}].transcripts.item`);
      }

      return null;
    }
  );

  const handleEnrollPodcast = () => {
    if (courseContext.createCourseContentSession) {
      courseContext.createCourseContentSession(content);
      setAutoPlay(true);
    }
  };

  const handleAnalyticsChange = (event: any) => {
    console.log("event", event);
    const podcast = learningRecordPodcast as unknown as LearningRecordPodcast;

    if (podcast && podcast.id && podcast.session && podcast.session.id) {
      const request = {
        course: course.course.id!,
        learningRecord: course.id!,
        id: content.id,
        type: content.entityType,
        session: session,
        dataPoints: event.analytics?.datapoints || [],
        percentCompleted: event.analytics?.percentageRead || 0,
        progress: event.analytics?.progress || 0,
        duration: event.analytics?.duration || 0,
        //sequence: new Date().getTime(),
      } as UpdateCourseContentSessionRequest;
  
      dispatch(updateCourseContentSession(request));
    }
  };

  useEffect(() => {
    if (childLearningRecord && !learningRecordPodcast) {
      dispatch(fetchLearningRecordPodcast(childLearningRecord));
    }else if(id && !publicPodcast) {
      dispatch(fetchPublicPodcast({ id: id }));
    }
  }, [childLearningRecord, id]);

  useEffect(() => {
    if (!childLearningRecord && podcast) {
      setPodcastContext((prev) => {
        return {
          ...prev,
          //   startPodcast: handleStartPodcast,
          onAnalyticsChange: handleAnalyticsChange,
          enrollPodcast: handleEnrollPodcast,
        };
      });

      setPodcastState((prev) => {
        return {
          ...prev,
          podcast: {
            ...podcast,
            id: podcast.id!,
            podcast: podcast,
            session: null,
            status: "NOT_ENROLLED",
          },
        };
      });
    }
  }, [childLearningRecord, podcast?.id]);

  useEffect(() => {
    if (podcastTranscripts) {
      setPodcastContext((prev) => {
        return {
          ...prev,
          transcripts: podcastTranscripts,
        };
      });
    }
  }, [podcastTranscripts]);

  useEffect(() => {
    setPodcastContext((prev) => {
      return {
        ...prev,
        enrollingPodcast: creating || (childLearningRecord && !learningRecordPodcast?.id),
      };
    });
  }, [creating, childLearningRecord, learningRecordPodcast?.id]);

  useEffect(() => {
    if (childLearningRecord && learningRecordPodcast) {
      dispatch(fetchTranscriptsForLearningPodcast(learningRecordPodcast.id));

      setPodcastContext((prev) => {
        return {
          ...prev,
          enrolledPodcastId: childLearningRecord,
          onAnalyticsChange: handleAnalyticsChange,
        };
      });

      setPodcastState((prev) => {
        return {
          ...prev,
          podcast: learningRecordPodcast
        };
      });
    }
  }, [childLearningRecord, learningRecordPodcast?.id]);

  if (loading) {
    return (
      <div className={styles.loading}>
        <img src="/images/loading_plane.gif" />
        <div className={styles.message}>Loading podcast...</div>
      </div>
    );
  }

  if (!loading && !podcast) {
    return (
      <div className={styles.notFoundContainer}>
        <EmptyMessage
          width={500}
          image="/images/assets/404error_message.svg"
          heading="Mmm... who moved my cheese?"
          message="Sorry! Someone passed you the wrong information. You were never supposed to see me :). This podcast does not exist any longer."
        />
      </div>
    );
  }

  return (
    <PodcastContext.Provider value={podcastContext}>
      {podcastState.podcast && (
        <PodcastSummaryComponent 
          podcast={podcastState.podcast} 
          className={styles.podcastView} 
          parentCourse={course} 
          autoPlay={autoPlay}
        />
      )}
    </PodcastContext.Provider>
  );
};
